import { Box, CircularProgress, Typography } from '@material-ui/core';
import Chart from 'react-apexcharts';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import hexToRgbA from 'src/utils/hexToRgbA';
import { toast } from 'react-hot-toast';
import { reportingApi } from 'src/api/reporting';

type BillingAccount = {
  id: string;
  name: string;
};

interface HourComparisonChartProps {
  selectedOrganization: {
    id: string;
    reportingLowerLimit?: number;
    reportingUpperLimit?: number;
  };
  billingAccountId: BillingAccount[];
}

const HourComparisonChart = ({
  selectedOrganization,
  billingAccountId,
}: HourComparisonChartProps) => {
  const [hourComparison, setHourComparison] = useState({
    series: [
      {
        name: 'Logged Hours',
        data: [],
      },
    ],
    options: {
      colors: [hexToRgbA('#c22027', 1)],
      grid: {
        strokeDashArray: 7,
        show: true,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        width: 3,
        curve: 'straight' as 'straight',
      },
      markers: {
        size: 4,
        colors: ['#fff'],
        strokeColors: hexToRgbA('#c22027', 1),
        strokeWidth: 2,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: [],
        axisBorder: {
          show: false,
        },
        title: {
          text: 'No Data',
          offsetY: -10,
          style: {
            fontFamily: 'Quicksand',
            fontSize: '14px',
            fontWeight: 500,
            color: '#7A7A9D',
          },
        },
        labels: {
          rotate: 0,
          style: {
            fontFamily: 'Quicksand',
            fontSize: '12px',
            fontWeight: 400,
            colors: '#7A7A9D',
          },
        },
        axisTicks: { show: false },
      },
      legend: {
        show: false,
      },
      yaxis: {
        labels: {
          style: {
            fontFamily: 'Quicksand',
            fontSize: '12px',
            fontWeight: 400,
            colors: '#7A7A9D',
          },
          formatter: (value) => value.toFixed(2),
        },
        min: selectedOrganization?.reportingLowerLimit ?? undefined,
        max: selectedOrganization?.reportingUpperLimit ?? undefined,
        lines: {
          show: true,
          colors: ['#FF0000', '#FF0000'],
          opacity: 0.3,
          value: [
            selectedOrganization?.reportingLowerLimit,
            selectedOrganization?.reportingUpperLimit,
          ].filter(Boolean),
        },
      },
      fill: {
        opacity: 1,
      },
    },
  });

  const [loading, setLoading] = useState(false);

  const processApprovedCasesData = (data) => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Logged Hours',
            data: [0],
          },
        ],
        options: {
          ...hourComparison.options,
          xaxis: {
            ...hourComparison.options.xaxis,
            categories: ['No Data'],
          },
        },
      };
    }

    const seriesData = data.map((item) => item.loggedHours || 0);
    const categories = data.map(
      (item) => item.sprintName.split(' ') || 'Unnamed Sprint'
    );

    return {
      series: [
        {
          name: 'Logged Hours',
          data: seriesData,
        },
      ],
      options: {
        ...hourComparison.options,
        xaxis: {
          categories,
          axisBorder: {
            show: false,
          },
          title: {
            text: 'Logged Hours',
            offsetY: 0,
            style: {
              fontFamily: 'Quicksand',
              fontSize: '14px',
              fontWeight: 500,
              color: '#7A7A9D',
            },
          },
          labels: {
            rotate: 0,
            style: {
              fontFamily: 'Quicksand',
              fontSize: '12px',
              fontWeight: 400,
              colors: '#7A7A9D',
            },
          },
          axisTicks: { show: false },
        },
        yaxis: {
          ...hourComparison.options.yaxis,
          min: selectedOrganization?.reportingLowerLimit ?? undefined,
          max: selectedOrganization?.reportingUpperLimit ?? undefined,
          // Add horizontal lines for limits if they exist
          lines: {
            show: true,
            colors: ['#FF0000', '#FF0000'], // Red lines for limits
            opacity: 0.3,
            value: [
              selectedOrganization?.reportingLowerLimit,
              selectedOrganization?.reportingUpperLimit,
            ].filter(Boolean), // Only add lines for defined limits
          },
        },
      },
    };
  };

  // Fetch chart data for the selected sprint and organization
  const fetchSprintWiseChartData = async () => {
    if (!selectedOrganization?.id) return;

    setLoading(true);
    try {
      const response = await reportingApi.getSprintWiseChartData({
        orgId: selectedOrganization.id,
        billingAccountId:
          billingAccountId?.length > 0
            ? billingAccountId.map((account) => account.id).join(',')
            : null,
      });
      // Process and set chart data
      if (response?.data) {
        setHourComparison(processApprovedCasesData(response.data.reverse()));
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      toast.error('Error fetching chart data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSprintWiseChartData();
  }, [selectedOrganization, billingAccountId]);

  return (
    <Box>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={350}>
          <CircularProgress />
        </Box>
      ) : (
        <Chart
          options={hourComparison.options}
          series={hourComparison.series}
          type="line"
          height={350}
        />
      )}
    </Box>
  );
};

export default HourComparisonChart;
